/**
 * Chat context: a conversation's history may grow up to this many tokens (or the model's own
 * window, when smaller) before older turns are compacted into a summary.
 */
export const CHAT_CONTEXT_MAX_TOKENS = 1_000_000;

/** Images users can attach to a chat message (screenshots). */
export const CHAT_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'] as const;
export type ChatImageType = (typeof CHAT_IMAGE_TYPES)[number];
/** Text and log files (e.g. the run page's "Download logs"). Never sent to the model in full: only metadata. */
export const CHAT_TEXT_TYPES = ['text/plain', 'application/json', 'application/x-ndjson'] as const;
export type ChatTextType = (typeof CHAT_TEXT_TYPES)[number];
export const CHAT_ATTACHMENT_TYPES = [...CHAT_IMAGE_TYPES, ...CHAT_TEXT_TYPES] as const;
export type ChatAttachmentType = (typeof CHAT_ATTACHMENT_TYPES)[number];
/** Allowed text file extensions and the media type each is stored as (the extension decides, not the browser). */
export const CHAT_TEXT_EXTENSIONS: Record<string, ChatTextType> = {
  ndjson: 'application/x-ndjson',
  log: 'text/plain',
  txt: 'text/plain',
  json: 'application/json',
};
export const CHAT_TEXT_EXTENSIONS_LABEL = '.ndjson, .log, .txt or .json';
export const isChatImageType = (t: string): t is ChatImageType =>
  (CHAT_IMAGE_TYPES as readonly string[]).includes(t);
export const isChatTextType = (t: string): t is ChatTextType =>
  (CHAT_TEXT_TYPES as readonly string[]).includes(t);
/** The text media type for a file name, or null when its extension is not allowed. */
export function textTypeForName(name: string): ChatTextType | null {
  const m = /\.([A-Za-z0-9]+)$/.exec(name);
  return (m && CHAT_TEXT_EXTENSIONS[m[1]!.toLowerCase()]) || null;
}
/** Per file. For images the browser downscales big screenshots before uploading, so this is rarely reached. */
export const CHAT_ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;
export const CHAT_ATTACHMENTS_PER_MESSAGE = 5;
/** Largest image a model provider accepts (Anthropic: 5 MB); bigger uploads stay visible in the chat but reach the model as a text note. */
export const CHAT_MODEL_IMAGE_MAX_BYTES = 5 * 1024 * 1024;
/** Human-readable limit for messages ("10 MB"). */
export const CHAT_ATTACHMENT_MAX_LABEL = `${CHAT_ATTACHMENT_MAX_BYTES / 1024 / 1024} MB`;

/** Platform tools for the files attached to the current chat (offered only when the chat has some). */
export const ATTACHMENT_LIST_TOOL = 'attachment_list';
export const ATTACHMENT_GET_TOOL = 'attachment_get';
export const IMAGE_VIEW_TOOL = 'image_view';
export const TRELLO_ATTACH_FILE_TOOL = 'trello_attach_file';

/** Where a chat attachment is placed in the agent's sandbox. */
export const CHAT_ATTACHMENTS_SANDBOX_DIR = '/workspace/attachments';

/** A file name that is safe on any filesystem and in a shell argument (no path separators, no control chars). */
export function safeAttachmentName(name: string): string {
  const base = name.split(/[\\/]/).pop() ?? '';
  const cleaned = base
    .normalize('NFKC')
    .replace(/[^A-Za-z0-9._ -]+/g, '_')
    .replace(/^[.\s-]+/, '')
    .trim()
    .slice(-100);
  return cleaned || 'image';
}

/**
 * `/workspace/attachments/<conversationId>/<id>-<name>`: the full id keeps pasted screenshots (all called
 * "image.png") apart, and the path is the same on every run so it can be quoted in earlier messages.
 */
export const attachmentSandboxPath = (conversationId: string, att: { id: string; name: string }) =>
  `${CHAT_ATTACHMENTS_SANDBOX_DIR}/${conversationId}/${att.id}-${safeAttachmentName(att.name)}`;
